import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, X, Folder, Bot, Lightbulb, Compass, ArrowRight } from 'lucide-react';
import { projectsData } from '../data/projectsData';
import { ideasData } from '../data/ideasData';

const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Build the unified search database
  const searchItems = useMemo(() => {
    const items = [];

    // 1. Static Navigation Pages
    items.push(
      { type: 'Page', id: 'page-home', title: 'Home', description: 'Portfolio overview and featured work', path: '/', icon: Compass, badgeColor: 'bg-purple-500/10 text-purple-300 border-purple-500/20' },
      { type: 'Page', id: 'page-projects', title: 'Projects', description: 'Complete showcase of engineered applications & hardware', path: '/projects', icon: Compass, badgeColor: 'bg-purple-500/10 text-purple-300 border-purple-500/20' },
      { type: 'Page', id: 'page-ideas', title: 'Research & Ideas', description: 'Scientific papers, proposals, and exploratory theoretical concepts', path: '/ideas', icon: Compass, badgeColor: 'bg-purple-500/10 text-purple-300 border-purple-500/20' },
      { type: 'Page', id: 'page-about', title: 'About', description: 'Background, technical skills, and research background', path: '/about', icon: Compass, badgeColor: 'bg-purple-500/10 text-purple-300 border-purple-500/20' }
    );

    // 2. Projects
    projectsData.forEach((project) => {
      if (project.id !== 'discordbots') {
        items.push({
          type: 'Project',
          id: `proj-${project.id}`,
          title: project.title,
          category: project.category,
          description: project.description,
          tech: project.tech,
          path: `/projects/${project.id}`,
          icon: Folder,
          badgeColor: 'bg-fuchsia-500/10 text-fuchsia-300 border-fuchsia-500/20'
        });
      } else if (project.bots && Array.isArray(project.bots)) {
        // Add individual Discord Bots
        project.bots.forEach((bot) => {
          items.push({
            type: 'Bot',
            id: `bot-${bot.slug || bot.id}`,
            title: bot.name,
            category: bot.tag || 'Discord Bot',
            description: bot.fullDescription || bot.description,
            tech: bot.tech,
            path: `/projects/discordbots/${bot.slug || bot.id}`,
            icon: Bot,
            badgeColor: 'bg-pink-500/10 text-pink-300 border-pink-500/20'
          });
        });
      }
    });

    // 3. Research & Ideas
    ideasData.forEach((idea) => {
      items.push({
        type: 'Research',
        id: `idea-${idea.id}`,
        title: idea.title,
        category: idea.category,
        description: idea.summary || idea.description,
        path: `/ideas/${idea.id}`,
        icon: Lightbulb,
        badgeColor: 'bg-purple-500/10 text-purple-200 border-purple-500/20'
      });
    });

    return items;
  }, []);

  // Filter items based on query
  const filteredResults = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    return searchItems.filter((item) => {
      const matchTitle = item.title?.toLowerCase().includes(q);
      const matchCategory = item.category?.toLowerCase().includes(q);
      const matchDesc = item.description?.toLowerCase().includes(q);
      const matchTech = item.tech?.toLowerCase().includes(q);
      const matchType = item.type?.toLowerCase().includes(q);
      return matchTitle || matchCategory || matchDesc || matchTech || matchType;
    });
  }, [query, searchItems]);

  // Reset selected index when filtered results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredResults]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle keyboard navigation within modal
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex((prev) => (filteredResults.length > 0 ? (prev + 1) % filteredResults.length : 0));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex((prev) => (filteredResults.length > 0 ? (prev - 1 + filteredResults.length) % filteredResults.length : 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (filteredResults[selectedIndex]) {
          navigate(filteredResults[selectedIndex].path);
          onClose();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, filteredResults, selectedIndex, navigate, onClose]);

  const handleSelect = (path) => {
    navigate(path);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-16 sm:pt-24 px-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/80 backdrop-blur-md"
          />

          {/* Modal Content */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="relative w-full max-w-2xl bg-[#090714] border border-purple-500/30 rounded-2xl shadow-[0_0_50px_rgba(232,121,249,0.15)] overflow-hidden z-10 flex flex-col max-h-[80vh]"
          >
            {/* Search Bar Header */}
            <div className="flex items-center px-4 py-3.5 border-b border-purple-500/20 bg-[#0d0a1c]">
              <Search className="w-5 h-5 text-purple-400 mr-3 shrink-0" />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search projects, bots, research, ideas..."
                className="w-full bg-transparent text-white placeholder-gray-500 focus:outline-none text-base font-medium"
              />
              {query && (
                <button
                  onClick={() => setQuery('')}
                  className="p-1 text-gray-400 hover:text-fuchsia-300 rounded-md transition-colors mr-1"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={onClose}
                className="text-xs font-semibold px-2 py-1 rounded-lg bg-purple-950/40 text-purple-300 hover:text-white border border-purple-500/30 transition-colors font-mono"
              >
                ESC
              </button>
            </div>

            {/* Results Body */}
            <div className="overflow-y-auto p-2 space-y-1 custom-scrollbar min-h-[200px]">
              {!query.trim() ? (
                <div className="py-12 text-center text-gray-500">
                  <Search className="w-10 h-10 mx-auto mb-3 opacity-30 stroke-[1.5] text-purple-400" />
                  <p className="text-sm font-medium text-gray-400">Type to search across the systems archive...</p>
                  <div className="mt-4 flex flex-wrap justify-center gap-2 text-xs text-purple-300/80 font-mono">
                    <span className="px-2.5 py-1 bg-purple-950/40 rounded-full border border-purple-500/20">Try &quot;Lyrika&quot;</span>
                    <span className="px-2.5 py-1 bg-purple-950/40 rounded-full border border-purple-500/20">Try &quot;Swift&quot;</span>
                    <span className="px-2.5 py-1 bg-purple-950/40 rounded-full border border-purple-500/20">Try &quot;Neuroscience&quot;</span>
                  </div>
                </div>
              ) : filteredResults.length === 0 ? (
                <div className="py-12 text-center text-gray-500">
                  <p className="text-sm font-medium text-gray-400">No results found for &quot;{query}&quot;</p>
                  <p className="text-xs text-gray-600 mt-1 font-mono">Try checking for typos or searching another term.</p>
                </div>
              ) : (
                filteredResults.map((item, index) => {
                  const Icon = item.icon;
                  const isSelected = index === selectedIndex;

                  return (
                    <div
                      key={item.id}
                      onClick={() => handleSelect(item.path)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`group flex items-center justify-between p-3 rounded-xl cursor-pointer transition-all duration-150 ${
                        isSelected ? 'bg-purple-600/20 border border-purple-500/40 shadow-inner' : 'hover:bg-purple-950/20 border border-transparent'
                      }`}
                    >
                      <div className="flex items-start gap-3 min-w-0">
                        <div className={`p-2 rounded-lg shrink-0 mt-0.5 ${isSelected ? 'bg-purple-500/20 text-fuchsia-300' : 'bg-purple-950/30 text-gray-400'}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-gray-200'}`}>
                              {item.title}
                            </span>
                            <span className={`text-[10px] uppercase tracking-wider font-bold font-mono px-2 py-0.5 rounded-md border ${item.badgeColor}`}>
                              {item.type}
                            </span>
                          </div>
                          {item.description && (
                            <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">
                              {item.description}
                            </p>
                          )}
                        </div>
                      </div>
                      <ArrowRight className={`w-4 h-4 shrink-0 ml-3 transition-transform duration-150 ${isSelected ? 'text-fuchsia-400 translate-x-1' : 'text-gray-600 opacity-0 group-hover:opacity-100'}`} />
                    </div>
                  );
                })
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-4 py-2.5 bg-[#0d0a1c] border-t border-purple-500/20 flex items-center justify-between text-[11px] font-mono text-gray-400">
              <div className="flex items-center gap-3">
                <span><kbd className="px-1.5 py-0.5 bg-purple-950/60 rounded text-purple-300 border border-purple-500/20">↑↓</kbd> Navigate</span>
                <span><kbd className="px-1.5 py-0.5 bg-purple-950/60 rounded text-purple-300 border border-purple-500/20">↵</kbd> Select</span>
              </div>
              <div>
                <span><kbd className="px-1.5 py-0.5 bg-purple-950/60 rounded text-purple-300 border border-purple-500/20">ESC</kbd> Close</span>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default SearchModal;
